import { useState } from "react";
import { 
  CheckCircle2, 
  Calendar, 
  Euro, 
  Mail, 
  Phone, 
  X, 
  User, 
  Copy, 
  Check,
  CreditCard,
  Building2,
  FileText,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CampRegistrationSuccess({ data, onClose }) {
  const [copiedField, setCopiedField] = useState(null);

  // IBAN and Verwendungszweck for copy functionality
  const IBAN = "DE21 5002 0406 4225 30";
  const verwendungszweck = `Camp ${data.participant_name}`;

  const copyToClipboard = async (text, fieldName) => {
    try {
      // Only remove spaces for IBAN, keep original text for other fields
      const textToCopy = fieldName === 'iban' ? text.replace(/\s/g, '') : text;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl my-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 md:top-2 md:right-2 w-10 h-10 flex items-center justify-center bg-[#1A1A1A] border border-white/20 rounded-full text-white/70 hover:text-white hover:bg-[#2A2A2A] transition-colors z-20"
          aria-label="Schließen"
          data-testid="camp-success-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 md:p-8">
          {/* Success Icon - Professional Check Circle */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-[#c1ff72]/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
              <div className="relative w-20 h-20 bg-[#c1ff72]/10 rounded-full flex items-center justify-center border-2 border-[#c1ff72]">
                <CheckCircle2 className="w-10 h-10 text-[#c1ff72]" />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
              Anmeldung erfolgreich!
            </h2>
            <p className="text-white/60 text-sm">
              Vielen Dank für deine Anmeldung. Wir haben alle Daten erhalten und melden uns schnellstmöglich per E-Mail.
            </p>
          </div>

          {/* Registration Details */}
          <div className="bg-[#1A1A1A] rounded-xl p-5 mb-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <span className="text-white/50 text-sm">Anmeldenummer</span>
              <span className="font-mono text-[#c1ff72] font-bold text-sm">{data.registration_id}</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
                <div>
                  <p className="text-white font-medium text-sm">{data.camp_name}</p>
                  <p className="text-white/60 text-xs">{data.camp_date}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
                <p className="text-white text-sm">{data.participant_name}</p>
              </div>
              
              <div className="flex items-center gap-3">
                <Euro className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
                <p className="text-white font-bold">{data.camp_price}€</p>
              </div>
            </div>
          </div>

          {/* Payment Information - Structured Card */}
          <div className="bg-[#1A1A1A] rounded-xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
              <CreditCard className="w-5 h-5 text-[#c1ff72]" />
              <h4 className="text-white font-medium">Zahlungsinformationen</h4>
            </div>

            <p className="text-white/70 text-sm mb-4">
              Bitte überweise den Betrag von <strong className="text-[#c1ff72]">{data.camp_price}€</strong> auf folgendes Konto:
            </p>

            <div className="space-y-3">
              {/* Zahlungsart */}
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Zahlungsart</p>
                  <p className="text-white text-sm">Vorkasse (Banküberweisung)</p>
                </div>
              </div>

              {/* Kontoinhaber */}
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Kontoinhaber</p>
                  <p className="text-white text-sm">Yasin Aktas</p>
                </div>
              </div>

              {/* IBAN with Copy Button */}
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white/50 text-xs uppercase tracking-wide">IBAN</p>
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-mono">{IBAN}</p>
                    <button
                      onClick={() => copyToClipboard(IBAN, 'iban')}
                      className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                      title="IBAN kopieren"
                    >
                      {copiedField === 'iban' ? (
                        <Check className="w-3.5 h-3.5 text-[#c1ff72]" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                    {copiedField === 'iban' && (
                      <span className="text-[#c1ff72] text-xs">Kopiert!</span>
                    )}
                  </div>
                </div>
              </div>

              {/* BIC */}
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">BIC</p>
                  <p className="text-white text-sm font-mono">DEFFDEFFXXX</p>
                </div>
              </div>

              {/* Verwendungszweck with Copy Button */}
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white/50 text-xs uppercase tracking-wide">Verwendungszweck</p>
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm">{verwendungszweck}</p>
                    <button
                      onClick={() => copyToClipboard(verwendungszweck, 'verwendungszweck')}
                      className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                      title="Verwendungszweck kopieren"
                    >
                      {copiedField === 'verwendungszweck' ? (
                        <Check className="w-3.5 h-3.5 text-[#c1ff72]" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                    {copiedField === 'verwendungszweck' && (
                      <span className="text-[#c1ff72] text-xs">Kopiert!</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PayPal Alternative */}
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="text-white/50 text-xs mb-2">Alternativ kannst du den Betrag bequem per PayPal bezahlen:</p>
              <a 
                href={`https://paypal.me/aktasyasin/${data.camp_price}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0070ba]/20 hover:bg-[#0070ba]/30 border border-[#0070ba]/40 rounded-lg px-4 py-2 text-[#0070ba] transition-colors"
              >
                <span className="font-medium text-sm">PayPal</span>
                <span className="text-white/70 text-sm">paypal.me/aktasyasin</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-[#1A1A1A] rounded-xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#c1ff72]" />
              <h4 className="text-white font-medium">Nächste Schritte</h4>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#c1ff72]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-3 h-3 text-[#c1ff72]" />
                </div>
                <span className="text-white/70 text-sm">Du erhältst eine Bestätigungsmail mit allen Details</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#c1ff72]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Euro className="w-3 h-3 text-[#c1ff72]" />
                </div>
                <span className="text-white/70 text-sm">Überweise den Betrag innerhalb von 7 Tagen</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#c1ff72]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Calendar className="w-3 h-3 text-[#c1ff72]" />
                </div>
                <span className="text-white/70 text-sm">Kurz vor dem Camp erhältst du alle wichtigen Infos</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center text-white/50 text-sm mb-5">
            <p className="mb-2">Fragen? Wir sind für dich da:</p>
            <div className="flex justify-center gap-4">
              <a href="tel:+491741944996" className="flex items-center gap-1 text-[#c1ff72] hover:underline">
                <Phone className="w-4 h-4" />
                +49 174 1944996
              </a>
              <a href="mailto:kontakt@fussballtraining-aktas.de" className="flex items-center gap-1 text-[#c1ff72] hover:underline">
                <Mail className="w-4 h-4" />
                E-Mail
              </a>
            </div>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            className="w-full bg-[#c1ff72] text-black hover:bg-[#d4ff9a] font-semibold"
          >
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
}
